import { useState } from "react";
import { Eye } from "lucide-react";
import { api } from "../lib/api.js";

/**
 * Pinned banner shown for the lifetime of an admin impersonation session.
 * `me.impersonator` is set server-side only on a session minted by
 * /api/admin/impersonate, so this renders exactly when (and only when) an
 * admin is "viewing as" another user. The switch-back button restores a fresh
 * admin session and hard-navigates to the console.
 */
export function ImpersonationBanner({
  viewingAs,
  adminEmail,
}: {
  viewingAs: string;
  adminEmail: string;
}) {
  const [busy, setBusy] = useState(false);

  async function stop() {
    setBusy(true);
    try {
      await api.stopImpersonating();
    } catch {
      /* even on error the cookie was cleared server-side — fall through */
    }
    window.location.href = "/admin";
  }

  return (
    <div
      data-testid="impersonation-banner"
      className="relative z-30 flex items-center justify-center gap-3 flex-wrap px-4 py-2 text-[13px] bg-[var(--color-mx-gold)]/12 border-b border-[var(--color-mx-gold)]/30 text-[var(--color-mx-gold)]"
    >
      <span className="inline-flex items-center gap-2">
        <Eye className="h-3.5 w-3.5" aria-hidden="true" />
        Viewing as <span className="font-medium">{viewingAs}</span>
        <span className="text-[var(--color-mx-gold)]/60 hidden sm:inline">
          · admin {adminEmail}
        </span>
      </span>
      <button
        type="button"
        onClick={stop}
        disabled={busy}
        data-testid="stop-impersonating"
        className="underline underline-offset-2 hover:no-underline disabled:opacity-60"
      >
        {busy ? "Switching back…" : "Return to admin"}
      </button>
    </div>
  );
}
